import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Optionally redirect to login
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Public
export const publicAPI = {
  getLooks: (params) => api.get('/public/looks', { params }),
  getLook: (slug) => api.get(`/public/looks/${slug}`),
  getProducts: (params) => api.get('/public/products', { params }),
  getProduct: (slug) => api.get(`/public/products/${slug}`),
  search: (q, params = {}) => api.get('/public/search', { params: { q, ...params } }),
};

// Cart
export const cartAPI = {
  create: () => api.post('/cart'),
  get: (cartId) => api.get('/cart', { params: { cart_id: cartId } }),
  addItem: (cartId, item) => api.post('/cart/items', item, { params: { cart_id: cartId } }),
  updateItem: (cartId, itemId, quantity) =>
    api.patch('/cart/items', { quantity }, { params: { cart_id: cartId, item_id: itemId } }),
  removeItem: (cartId, itemId) =>
    api.delete('/cart/items', { params: { cart_id: cartId, item_id: itemId } }),
  applyPromo: (cartId, promoCode) =>
    api.post('/cart/promo', { promo_code: promoCode }, { params: { cart_id: cartId } }),
};

// Checkout
export const checkoutAPI = {
  createOrder: (data) => api.post('/checkout', data),
  getOrder: (orderId) => api.get(`/checkout/${orderId}`),
};

// Payments
export const paymentAPI = {
  createVivaPayment: (orderId) => api.post('/payments/viva/create', { order_id: orderId }),
  createPayPalPayment: (orderId) => api.post('/payments/paypal/create', { order_id: orderId }),
  mockSuccess: (orderId) => api.post('/payments/mock-success', { order_id: orderId }),
};

// Leads
export const leadAPI = {
  create: (data) => api.post('/leads', data),
};

// Events tracking
export const trackEvent = (eventType, eventData = {}) => {
  return api.post('/events', {
    event_type: eventType,
    event_data: eventData,
    session_id: getSessionId(),
  }).catch(() => { }); // Silent fail for tracking
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboard: (days = 30) => api.get('/admin/dashboard', { params: { days } }),

  // Orders
  getOrders: (params) => api.get('/admin/orders', { params }),
  getOrder: (id) => api.get(`/admin/orders/${id}`),
  updateOrder: (id, data) => api.patch(`/admin/orders/${id}`, data),
  assignOrderToMe: (id) => api.post(`/admin/orders/${id}/assign-to-me`),
  refundOrder: (id) => api.post(`/admin/orders/${id}/refund`),

  // Products
  getProducts: (params) => api.get('/admin/products', { params }),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),

  // Looks
  getLooks: (params) => api.get('/admin/looks', { params }),
  createLook: (data) => api.post('/admin/looks', data),
  updateLook: (id, data) => api.put(`/admin/looks/${id}`, data),
  deleteLook: (id) => api.delete(`/admin/looks/${id}`),

  // Leads
  getLeads: (params) => api.get('/admin/leads', { params }),

  // Email Templates
  getEmailTemplates: () => api.get('/admin/email-templates'),
  createEmailTemplate: (data) => api.post('/admin/email-templates', data),
  updateEmailTemplate: (id, data) => api.put(`/admin/email-templates/${id}`, data),
  resetEmailTemplates: () => api.post('/admin/email-templates/reset'),

  // Promotions
  getPromotions: () => api.get('/admin/promotions'),
  createPromotion: (data) => api.post('/admin/promotions', data),
  updatePromotion: (id, data) => api.put(`/admin/promotions/${id}`, data),
  deletePromotion: (id) => api.delete(`/admin/promotions/${id}`),

  // Staff
  listStaff: () => api.get('/staff'),
  createStaff: (data) => api.post('/staff', data),
  updateStaff: (id, data) => api.put(`/staff/${id}`, data),
  deleteStaff: (id) => api.delete(`/staff/${id}`),
  sendResetEmail: (id) => api.post(`/staff/${id}/reset-password`),
};

// Conversations API
export const conversationsAPI = {
  list: (params) => api.get('/conversations', { params }),
  get: (id) => api.get(`/conversations/${id}`),
  create: (data) => api.post('/conversations', data),
  sendMessage: (id, content, product_ids = [], look_ids = []) =>
    api.post(`/conversations/${id}/messages`, { content, product_ids, look_ids }),
  assign: (id) => api.post(`/conversations/${id}/assign`),
  close: (id) => api.post(`/conversations/${id}/close`),
  cleanup: () => api.delete('/conversations/cleanup'),
};

// Seed database
export const seedDatabase = () => api.post('/seed');

// Session ID helper
function getSessionId() {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
}

// Cart ID helper
export function getCartId() {
  return localStorage.getItem('cart_id');
}

export function setCartId(id) {
  localStorage.setItem('cart_id', id);
}

export default api;
